import { Exercise } from "./../../../models/Exercise";
import { addDays, format, startOfDay, startOfWeek } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../utils/hooks/useFacility";
import { GET_SCHEDULE_EXERCISES_V2 } from "../../../api/apiRoutes";
import { useSelector } from "react-redux";
import { State } from "../../../store";
import { ExerciseScheduleList } from "../../../models/ExerciseScheduleList";
import { ExerciseFiltersObject } from "./useAdditionalHooks";
import getId from "../../../../utils/getId";
import { parseScheduleDate } from "../scheduleOptions";

const exerciseHasFreeSpot = (exercise: Exercise) => {
  const totalReserved = exercise.reserved + exercise.prereservation;
  return exercise.capacity - totalReserved > 0;
};

export class Hours {
  constructor(data: any) {
    this.time = data.time;
    this.days = data.days;
  }
  time: string;
  days: Days[];
}

export class Days {
  constructor(data: any) {
    this.day = data.day;
    this.exercise = data.exercise;
  }
  day: string;
  exercise: Exercise[];
}

export interface UseScheduleOptions {
  dateFromValue?: string;
  dateToValue?: string;
  initialFilters?: ExerciseFiltersObject;
}

const getInitialWeek = (minDate?: Date, maxDate?: Date) => {
  let from = startOfWeek(minDate ?? new Date(), { weekStartsOn: 1 });
  if (maxDate) {
    const maxWeekStart = startOfWeek(maxDate, { weekStartsOn: 1 });
    if (from.getTime() > maxWeekStart.getTime()) from = maxWeekStart;
  }
  return { from, to: addDays(from, 6) };
};

const getInitialMobileDay = (minDate?: Date, maxDate?: Date) => {
  let day = startOfDay(minDate ?? new Date());
  if (maxDate && day.getTime() > startOfDay(maxDate).getTime()) day = startOfDay(maxDate);
  return day;
};

export default function useSchedule(options?: UseScheduleOptions) {
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const userDataId = useSelector((state: State) => state.auth.userData.objectId);
  const today = new Date();
  const minDate = useMemo(() => parseScheduleDate(options?.dateFromValue), [options?.dateFromValue]);
  const maxDate = useMemo(() => parseScheduleDate(options?.dateToValue), [options?.dateToValue]);
  const defaultFiltersRef = useRef(options?.initialFilters ?? ({} as ExerciseFiltersObject));

  // Stan dla widoku tygodniowego
  const [weekRange, setWeekRange] = useState<{ from: Date; to: Date }>(() => getInitialWeek(minDate, maxDate));

  // Stan dla widoku mobilnego (dniowego)
  const [mobileRange, setMobileRange] = useState<{ from: Date; to: Date }>(() => {
    const day = getInitialMobileDay(minDate, maxDate);
    return { from: day, to: day };
  });

  const [exercisesList, setExercisesList] = useState<ExerciseScheduleList>({
    Poniedziałek: [],
    Wtorek: [],
    Środa: [],
    Czwartek: [],
    Piątek: [],
    Sobota: [],
    Niedziela: [],
  });
  const [filters, setFilters] = useState<ExerciseFiltersObject>(defaultFiltersRef.current);
  const [loading, setLoading] = useState(false);

  const fetchSchedule = useCallback(
    async (from: Date, to: Date, disableLoading?: boolean) => {
      let rangeFrom = from;
      let rangeTo = to;
      if (minDate && startOfDay(rangeFrom).getTime() < startOfDay(minDate).getTime()) {
        rangeFrom = startOfDay(minDate);
      }
      if (maxDate && startOfDay(rangeTo).getTime() > startOfDay(maxDate).getTime()) {
        rangeTo = startOfDay(maxDate);
      }
      if (rangeFrom.getTime() > rangeTo.getTime()) rangeTo = rangeFrom;

      const formattedFrom = format(rangeFrom, "yyyy-MM-dd");
      const formattedTo = format(rangeTo, "yyyy-MM-dd");

      if (!disableLoading) {
        setLoading(true);
      }
      const { result, isError } = await get(
        GET_SCHEDULE_EXERCISES_V2(
          facilityIds,
          formattedFrom,
          formattedTo,
          getId(filters.exerciseGroups),
          getId(filters.zones),
          getId(filters.humans),
          userDataId
        )
      );
      if (!isError && result) {
        const updatedSchedule: ExerciseScheduleList = {
          Poniedziałek: result["Poniedziałek"] || [],
          Wtorek: result["Wtorek"] || [],
          Środa: result["Środa"] || [],
          Czwartek: result["Czwartek"] || [],
          Piątek: result["Piątek"] || [],
          Sobota: result["Sobota"] || [],
          Niedziela: result["Niedziela"] || [],
        };
        setExercisesList(updatedSchedule);
      }
      setLoading(false);
    },
    [facilityIds, filters.exerciseGroups, filters.zones, filters.humans, get, userDataId, minDate, maxDate]
  );

  // Sterowanie zakresem tygodniowym
  const nextWeek = useCallback(() => {
    setWeekRange((prev) => {
      const newFrom = addDays(prev.from, 7);
      if (maxDate && newFrom.getTime() > startOfDay(maxDate).getTime()) return prev;
      const newTo = addDays(prev.to, 7);
      fetchSchedule(newFrom, newTo);
      return { from: newFrom, to: newTo };
    });
  }, [fetchSchedule, maxDate]);

  const previousWeek = useCallback(() => {
    setWeekRange((prev) => {
      const newFrom = addDays(prev.from, -7);
      if (minDate && newFrom.getTime() < startOfWeek(minDate, { weekStartsOn: 1 }).getTime()) return prev;
      const newTo = addDays(prev.to, -7);
      fetchSchedule(newFrom, newTo);
      return { from: newFrom, to: newTo };
    });
  }, [fetchSchedule, minDate]);

  const selectMobileDate = useCallback(
    (selectedDate: Date) => {
      let day = startOfDay(selectedDate);
      if (minDate && day.getTime() < startOfDay(minDate).getTime()) day = startOfDay(minDate);
      if (maxDate && day.getTime() > startOfDay(maxDate).getTime()) day = startOfDay(maxDate);
      setMobileRange({ from: day, to: day });
      fetchSchedule(day, day);
    },
    [fetchSchedule, minDate, maxDate]
  );

  const selectDesktopDate = useCallback(
    (selectedDate: Date) => {
      let day = startOfDay(selectedDate);
      if (minDate && day.getTime() < startOfDay(minDate).getTime()) day = startOfDay(minDate);
      if (maxDate && day.getTime() > startOfDay(maxDate).getTime()) day = startOfDay(maxDate);
      let weekStart = startOfWeek(day, { weekStartsOn: 1 });
      if (minDate) {
        const minWeekStart = startOfWeek(minDate, { weekStartsOn: 1 });
        if (weekStart.getTime() < minWeekStart.getTime()) weekStart = minWeekStart;
      }
      if (maxDate && weekStart.getTime() > startOfDay(maxDate).getTime()) {
        weekStart = startOfWeek(maxDate, { weekStartsOn: 1 });
      }
      const weekEnd = addDays(weekStart, 6);
      setWeekRange({ from: weekStart, to: weekEnd });
      fetchSchedule(weekStart, weekEnd);
    },
    [fetchSchedule, minDate, maxDate]
  );

  // Sterowanie zakresem dniowym (mobilnym)
  const nextDay = useCallback(() => {
    setMobileRange((prev) => {
      const newFrom = addDays(prev.from, 1);
      if (maxDate && startOfDay(newFrom).getTime() > startOfDay(maxDate).getTime()) return prev;
      const newTo = addDays(prev.to, 1);
      fetchSchedule(newFrom, newTo);
      return { from: newFrom, to: newTo };
    });
  }, [fetchSchedule, maxDate]);

  const previousDay = useCallback(() => {
    setMobileRange((prev) => {
      const newFrom = addDays(prev.from, -1);
      if (minDate && startOfDay(newFrom).getTime() < startOfDay(minDate).getTime()) return prev;
      const newTo = addDays(prev.to, -1);
      fetchSchedule(newFrom, newTo);
      return { from: newFrom, to: newTo };
    });
  }, [fetchSchedule, minDate]);

  // Funkcja inicjująca pobranie harmonogramu – wywoływana w komponencie nadrzędnym
  const getFirstAllExercises = useCallback(() => {
    fetchSchedule(weekRange.from, weekRange.to);
  }, [fetchSchedule, weekRange]);

  // Dodajemy efekt reagujący na zmiany filtrów API (bez onlyWithFreeSpots — to filtr po stronie klienta)
  useEffect(() => {
    if (facilityIds) {
      fetchSchedule(weekRange.from, weekRange.to);
    }
  }, [
    filters.exerciseGroups,
    filters.zones,
    filters.humans,
    facilityIds,
    weekRange.from,
    weekRange.to,
    fetchSchedule,
  ]);

  const exercisesListForView = useMemo(() => {
    if (!filters.onlyWithFreeSpots) {
      return exercisesList;
    }
    const days: (keyof ExerciseScheduleList)[] = [
      "Poniedziałek",
      "Wtorek",
      "Środa",
      "Czwartek",
      "Piątek",
      "Sobota",
      "Niedziela",
    ];
    return days.reduce((acc, day) => {
      acc[day] = exercisesList[day].filter(exerciseHasFreeSpot);
      return acc;
    }, {} as ExerciseScheduleList);
  }, [exercisesList, filters.onlyWithFreeSpots]);

  const minWeekStart = minDate ? startOfWeek(minDate, { weekStartsOn: 1 }) : undefined;
  const canGoPreviousWeek = !minWeekStart || weekRange.from.getTime() > minWeekStart.getTime();
  const canGoNextWeek = !maxDate || addDays(weekRange.from, 7).getTime() <= startOfDay(maxDate).getTime();
  const canGoPreviousDay = !minDate || startOfDay(mobileRange.from).getTime() > startOfDay(minDate).getTime();
  const canGoNextDay = !maxDate || startOfDay(mobileRange.from).getTime() < startOfDay(maxDate).getTime();

  return {
    getFirstAllExercises,
    previousDay,
    nextDay,
    nextWeek,
    previousWeek,
    canGoPreviousWeek,
    canGoNextWeek,
    canGoPreviousDay,
    canGoNextDay,
    minDate,
    maxDate,
    defaultFilters: defaultFiltersRef.current,
    dayFromStateMobile: mobileRange.from,
    dayToStateMobile: mobileRange.to,
    dateFromState: weekRange.from,
    dateToState: weekRange.to,
    today,
    exercisesList: exercisesListForView,
    loading,
    refreshExercisesList: (isMobile?: boolean) =>
      fetchSchedule(isMobile ? mobileRange.from : weekRange.from, isMobile ? mobileRange.to : weekRange.to, true),
    filters,
    setFilters,
    selectMobileDate,
    selectDesktopDate,
  };
}
